import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, InputSignal, OutputEmitterRef, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';

import { Pagination, PaginationMetadata } from '@core/shared/domain';
import { ReactiveMap } from '@core/shared/util';
import { PaginatorComponent } from '@core/ui';
import {
  IndustryCatalogManufacturer,
  IndustryCatalogOpenTasks,
  IndustryCatalogSupplier,
  PackungseinheitenMapping,
  UpdateItem,
  UpdateUnit,
} from '@mp/content-manager/industriekataloge/data-access';
import { TaskInfoComponent } from '@mp/shared/ui';
import { CatalogSupplierSelectorComponent } from '@mp/supplier-management/common/ui';
import { PlatformSupplierBasic } from '@mpsm/shared/domain';

import { HerstellerColumnName, LieferantColumnName, PackungseinheitenColumnName } from './column-name';
import { PackagingUnitColComponent } from './packaging-unit-col/packaging-unit-col.component';

export type EntityPaginationEvent = {
  isLieferantPagination: boolean;
  pagination: Pagination;
};

export interface NavigateTo {
  elementId: '' | 'supplier' | 'manufacturer' | 'packagingUnits';
}

@Component({
  selector: 'mp-tab-aufgaben',
  templateUrl: './tab-aufgaben.component.html',
  styleUrl: './tab-aufgaben.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,

    MatCard,
    MatIcon,
    MatIconButton,
    MatTableModule,

    CatalogSupplierSelectorComponent,
    PaginatorComponent,
    TaskInfoComponent,
    PackagingUnitColComponent,
  ],
})
export class TabAufgabenComponent {
  assignedLieferant = new ReactiveMap<string, string | undefined>({});

  readonly lieferanten: InputSignal<IndustryCatalogSupplier[]> = input.required<IndustryCatalogSupplier[]>();
  readonly hersteller: InputSignal<IndustryCatalogManufacturer[]> = input.required<IndustryCatalogManufacturer[]>();
  readonly packungseinheiten: InputSignal<PackungseinheitenMapping> = input.required<PackungseinheitenMapping>();
  readonly lieferantenPagination: InputSignal<PaginationMetadata | undefined> = input<PaginationMetadata | undefined>();
  readonly herstellerPagination: InputSignal<PaginationMetadata | undefined> = input<PaginationMetadata | undefined>();
  readonly packagingUnitPagination: InputSignal<PaginationMetadata | undefined> = input<
    PaginationMetadata | undefined
  >();
  readonly openTasks: InputSignal<IndustryCatalogOpenTasks | undefined> = input<IndustryCatalogOpenTasks | undefined>();
  readonly canWrite: InputSignal<boolean> = input<boolean>(false);
  readonly platformSuppliers: InputSignal<PlatformSupplierBasic[]> = input<PlatformSupplierBasic[]>([]);
  readonly isLoadingPlatformSuppliers: InputSignal<boolean> = input<boolean>(false);

  readonly schreibrecht4Packungseinheiten: InputSignal<boolean> = input<boolean>(false);

  readonly saveClicked: OutputEmitterRef<UpdateItem> = output<UpdateItem>();
  readonly saveEinheitenMapping: OutputEmitterRef<UpdateUnit> = output<UpdateUnit>();
  readonly deleteClicked: OutputEmitterRef<UpdateItem> = output<UpdateItem>();
  readonly pageChanges: OutputEmitterRef<EntityPaginationEvent> = output<EntityPaginationEvent>();
  readonly packagingUnitPageChanges: OutputEmitterRef<Pagination> = output<Pagination>();
  readonly supplierSearchTermChange: OutputEmitterRef<string> = output<string>();

  lieferantenCols: LieferantColumnName[] = ['lieferant', 'artikel', 'bezugsquelle'];
  herstellerCols: HerstellerColumnName[] = ['hersteller', 'artikel', 'bezugsquelle'];
  einheitenCols: PackungseinheitenColumnName[] = ['einheit', 'artikel', 'bezugsquelle'];

  navigateToElementId(navigateTo: NavigateTo): void {
    document.getElementById(navigateTo.elementId)?.scrollIntoView({ behavior: 'smooth' });
  }

  onLieferantChanges(bezeichnung: string, lieferant?: PlatformSupplierBasic): void {
    this.assignedLieferant?.set(bezeichnung, lieferant?.supplierId ?? undefined);
  }

  saveBezugsquelle(isSupplierUpdated: boolean, designation: string): void {
    this.saveClicked.emit({
      isSupplierUpdated,
      designation,
      platformSupplierId: this.assignedLieferant.get(designation),
    });
  }

  deleteBezugsquelle(isSupplierUpdated: boolean, designation: string): void {
    this.deleteClicked.emit({ isSupplierUpdated, designation });
  }

  emitPageChanges(isLieferantPagination: boolean, pagination: Pagination): void {
    this.pageChanges.emit({ isLieferantPagination, pagination });
  }

  emitPackagingUnitPageChanges(pageChangePagination: Pagination): void {
    this.packagingUnitPageChanges.emit(pageChangePagination);
  }

  saveEinheit({ unit, mappedShortName }: UpdateUnit): void {
    this.saveEinheitenMapping.emit({ unit, mappedShortName });
  }
}
